
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/use-auth';

export class NoSubscriptionError extends Error {
  constructor() {
    super('No subscription found for company. Please subscribe to a plan.');
    this.name = 'NoSubscriptionError';
  }
}

export function useBilling() {
  const { user } = useAuth();
  const companyId = user?.companyId;

  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['billing', 'subscription', companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error('No company ID available');
      }
      try {
        return await apiClient.billing.getBillingSubscriptionByCompany({ companyId });
      } catch (error: unknown) {
        // Backend contract: 404 means no subscription exists for this company.
        const err = error as { response?: { status?: number }; status?: number };
        const status = err?.response?.status ?? err?.status;
        if (status === 404) {
          throw new NoSubscriptionError();
        }
        throw error;
      }
    },
    enabled: !!companyId,
  });

  // Extract tier info - throw if accessed without a valid subscription
  const getCurrentTierCode = (): "free" | "starter" | "pro" | "expert" => {
    if (!subscription?.tier?.code) {
      throw new NoSubscriptionError();
    }
    return subscription.tier.code as "free" | "starter" | "pro" | "expert";
  };

  const getCreditRate = (): number => {
    if (!subscription?.tier?.creditRateCents) {
      throw new NoSubscriptionError();
    }
    return subscription.tier.creditRateCents / 100;
  };

  return {
    subscription,
    isLoading,
    error,
    // These getters throw if no subscription - fail fast!
    get currentTierCode() {
      return getCurrentTierCode();
    },
    get creditRate() {
      return getCreditRate();
    },
    // Check methods for conditional logic
    hasSubscription: !!subscription,
  };
}

/**
 * Hook to estimate supplement processing cost based on unmatched line items
 */
export function useEstimateSupplementCost() {
  return useMutation({
    mutationFn: async (unmatchedLineItemCount: number) => {
      return apiClient.billing.estimateSupplementCost({
        estimateSupplementCostRequest: {
          unmatchedLineItemCount
        }
      });
    },
  });
}
